import BannerMobile from '../assets/security-and-trust-header-mobile.png';
import BannerDesktop from '../assets/security-and-trust-header.png';
import CookiePolicy1 from '../assets/security-icon-01.png';
import CookiePolicy2 from '../assets/security-icon-02.png';
import CookiePolicy3 from '../assets/security-icon-03.png';
import CookiePolicy4 from '../assets/security-icon-04.png';
import CookiePolicy5 from '../assets/security-icon-05.png';
import { ContentSection } from '../components/content-section';
import { Layout } from '../components/page-layout';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
`;
const BannerImgMobile = styled.img`
  display: block;
  width: 100%;
  @media (min-width: 768px) {
    display: none;
  }
`;
const BannerImgDesktop = styled.img`
  display: none;
  width: 100%;
  @media (min-width: 768px) {
    display: block;
  }
`;
const Title = styled.h3`
  font-size: 1.875rem;
  font-weight: bold;
`;
const SubText = styled.p`
  font-size: 0.75rem;
`;
const MainParagraph = styled.p`
  padding: 1rem 0;
`;
const HighlightSection = styled.div`
  padding: 1.5rem;
  margin-top: 3rem;
  background: #F87B15;
  color: #fff;
  border-radius: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const HighlightText = styled.p`
  text-align: center;
  color: #fff;
  line-height: 1.5;
`;


const HighlightButton = styled.a`
  background: #fff;
  color: #F87B15;
  border-radius: 9999px;
  padding: 0.5rem 2rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  font-size: 1rem;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #ffe3d0;
    color: #d65c00;
  }
`;

export default function SecurityAndTrust() {
  return (
    <Layout showCommonFooter={true}>
      <Container>
        <Title>Sensforge Customer Data Security</Title>
        <SubText>Effective Date: Jan 1st 2025</SubText>
        <MainParagraph>
          At Sensforge, safeguarding customer privacy and data security is one
          of our most critical responsibilities. We are committed to ensuring
          that your personal data and video footage always remain secure and
          private at all times. 
        </MainParagraph>
        <BannerImgMobile src={BannerMobile} alt="Cookie Policy Banner" />
        <BannerImgDesktop src={BannerDesktop} alt="Cookie Policy Banner" />
        <ContentSection
          title="Your Data is Yours Alone"
          icon={CookiePolicy1}
          className="mt-6"
        >
          <p>
            We want to assure you that we never sell your data. Your personal
            information and video footage will never be shared, sold, or misused
            under any circumstances. Only minimal, anonymous data is shared with
            our cloud service provider to support the core functionalities of
            our products. Your trust is our priority, and we take every measure
            to protect it. 
          </p>
        </ContentSection>
        <ContentSection
          title="Advanced On-Chip Security Features "
          icon={CookiePolicy2}
          className="mt-6"
        >
          <p>
            Your data security is guaranteed by the advanced security features
            embedded in our custom-designed smart camera SoC (System on Chip).
            This SoC is engineered with a comprehensive suite of cutting-edge
            security technologies to ensure the highest levels of data
            protection and system integrity. Key features include: 
          </p>
          <ul>
            <li>Secure Boot: Ensures that only trusted software is executed during the boot process, preventing unauthorized or malicious code from running. This guarantees that the camera’s firmware and operating system are secure from the moment the device powers on. </li>
            <li>Hardware-Based Security Isolation: A dedicated security engine handles cryptographic operations securely and efficiently, while ARM TrustZone technology provides robust hardware isolation between the Rich Execution Environment (REE) and the Trusted Execution Environment (TEE). This ensures that sensitive operations, such as video encryption and secure key storage, are performed in a protected environment, even if the main OS is compromised. </li>
            <li>Hardware-Accelerated Cryptography: The SoC supports advanced cryptographic algorithms (AES, HASH, RSA) for encryption, decryption, and data integrity verification. AES ensures that video streams are encrypted before transmission, protecting them from interception or tampering. HASH algorithms (SHA-1/256) and HMAC (HMAC_SHA-1/256) verify data integrity, ensuring it has not been altered during transmission. </li>
            <li>Hardware Random Number Generator: Generates secure keys for encryption, ensuring strong cryptographic protection. </li>
            <li>16Kbit OTP (One-Time Programmable) Memory: Provides secure storage for sensitive information, such as device credentials, encryption keys, and certificates. This ensures that even if the device is physically compromised, critical security parameters cannot be altered or extracted. </li>
          </ul>
        </ContentSection>
        <ContentSection
          title="Comprehensive Data Security Framework  "
          icon={CookiePolicy3}
          className="mt-6"
        >
          <p>
            To protect your video data, we have implemented a multi-layered
            security framework that spans from the hardware level to the cloud.
            This framework ensures end-to-end protection for your data, from the
            moment it is captured by the camera to its storage and processing in
            the cloud. Key measures include: 
          </p>
          <ul>
            <li><strong>1. Secure Data Capture: </strong>Video data is encrypted at the hardware level using AES before it leaves the camera. Even if intercepted, the data cannot be decrypted without the proper keys. </li>
            <li><strong>2. Secure Communication: </strong>The SoC supports secure communication protocols (e.g., TLS/SSL) to establish encrypted channels between the camera, the mobile app, and the cloud. This prevents man-in-the-middle attacks and ensures that data is transmitted securely. Even if a hacker intercepts the data, it cannot be decrypted.</li>
            <li><strong>3. Authentication and Integrity:</strong> Using HMAC and SHA-256, the SoC ensures mutual authentication between the camera and the cloud server. Each camera has its own unique secret key and certificate, validating its identity during the handshake process. This ensures that the data has not been tampered with during transmission. </li>
            <li><strong>4.Secure Cloud Storage:</strong> Encrypted video data is securely stored in the cloud, with access controlled by cryptographic keys managed in the SoC’s TEE. Only authorized users or systems can decrypt and access the data. </li>
            <li><strong>5.Secure Local Storage:</strong> Our mobile app offers an optional SD Card Encryption feature. When enabled, video and image data stored on the SD card in your smart camera are encrypted and can only be decrypted and viewed using the mobile app. This prevents unauthorized access to your data. </li>
          </ul>
        </ContentSection>
        <ContentSection
          title="Enhanced System Security"
          icon={CookiePolicy4}
          className="mt-6"
        >
          <p>
            These features collectively enhance the security of our smart camera
            system by: 
          </p>
          <ul>
            <li>Preventing unauthorized access to the device firmware and software through Secure Boot. </li>
            <li>Hardware-Based Security Isolation: A dedicated security engine handles cryptographic operations securely and efficiently, while ARM TrustZone technology provides robust hardware isolation between the Rich Execution Environment (REE) and the Trusted Execution Environment (TEE). This ensures that sensitive operations, such as video encryption and secure key storage, are performed in a protected environment, even if the main OS is compromised. </li>
            <li>Isolating and protecting sensitive operations (e.g., encryption, key management) in the TrustZone TEE.  </li>
            <li>Ensuring video data is encrypted and tamper-proof during transmission and storage.  </li>
            <li>Providing secure storage for credentials and keys in OTP memory, preventing physical attacks.  </li>
            <li>Enabling secure, authenticated communication between the camera, the mobile app, and the cloud. </li>
            <li>Encrypting data on the SD card to prevent unauthorized access. </li>
          </ul>
        </ContentSection>
        <ContentSection
          title="Our Commitment to You "
          icon={CookiePolicy5}
          className="mt-6"
        >
          <p>
            By integrating these advanced security features, we have built
            a comprehensive data security framework that delivers robust
            protection for your video data. Our smart camera system meets the
            highest standards of privacy and security, ensuring that your data
            remains safe and confidential. At  Sensforge, we are dedicated to
            providing you with innovative technology that you can trust.  
          </p>
        </ContentSection>
        <HighlightSection>
          <HighlightText>
            For questions or concerns about this Customer Data Security, <br />
            please contact us at: <br />
            Email: legal@sensforge.com <br />
            Sensforge Inc. <br />
            639 Alpha Drive, Pittsburgh, PA 15238 <br />
          </HighlightText>
          <HighlightButton
            href="mailto:legal@sensforge.com"
            type="button"
          >
            Contact Us
          </HighlightButton>
        </HighlightSection>
      </Container>
    </Layout>
  );
}
